import React, { useEffect, useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { ricetteOfflineService } from '../services/ricetteOffline';
import { Gusto } from '../types';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  PageContainer,
  PageTitle,
  Button,
  Card,
  CardBody,
  Label,
  Input,
} from '../components/ui';

const Filters = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-md);
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const SelectList = styled.div`
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-md);
  padding: var(--space-sm);
  max-height: 280px;
  overflow: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.35rem 0.4rem;
  border-radius: var(--border-radius-sm);
  &:hover { background: var(--gray-50); }
`;

const Actions = styled.div`
  display: flex;
  gap: var(--space-sm);
  flex-wrap: wrap;
  margin-top: var(--space-md);
`;

// Stili per la stampa
const PrintContainer = styled.div`
  @media print {
    .no-print { display: none !important; }
    .page { page-break-after: always; break-after: page; }
    body { background: white; }
    * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
  }
`;

const PrintableContent = styled.div`
  background: white;
  color: black;
  font-family: Arial, sans-serif;
  
  .page {
    padding: 2rem;
    min-height: calc(100vh - 4rem);
    page-break-after: always;
    position: relative;
    /* Filigrana come background per essere catturata da html2canvas */
    background-image: 
      repeating-linear-gradient(
        45deg,
        transparent,
        transparent 70px,
        rgba(0,0,0,0.03) 70px,
        rgba(0,0,0,0.03) 71px
      );
    background-size: 100px 100px;
  }
  
  .page:last-child {
    page-break-after: auto;
  }
  
  /* Tabelle più trasparenti per mostrare la filigrana di background */
  table {
    background: transparent !important;
    border-collapse: separate;
    border-spacing: 0;
  }
  
  table th,
  table td {
    background: rgba(255, 255, 255, 0.85) !important; /* Leggermente più opaco ma trasparente */
    border: 1px solid rgba(222, 226, 230, 0.8);
  }
  
  table thead th {
    background: rgba(248, 249, 250, 0.9) !important;
    font-weight: bold;
  }
`;

const GustoPage = styled.div<{ $nomeAttivita: string }>`
  background: white;
  border: 1px solid #f1f3f5;
  border-radius: 8px;
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  position: relative;
  page-break-before: always; /* Ogni gusto inizia in una nuova pagina */
  page-break-after: always;  /* Assicura separazione */
  min-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
  
  /* Filigrana di testo sovrapposta: il nome e' quello dell'attivita' che sta
     stampando, non uno scritto nel codice — il sistema e' multi-tenant. */
  &::before {
    content: "© ${new Date().getFullYear()} " attr(data-attivita);
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
    font-size: 24px;
    color: rgba(0, 0, 0, 0.05);
    font-weight: bold;
    pointer-events: none;
    z-index: 1;
    white-space: nowrap;
  }
  
  &:first-child {
    page-break-before: auto; /* Il primo gusto non ha interruzione prima */
  }
  
  &.printable {
    border: none;
    border-radius: 0;
    margin-bottom: 0;
    padding: 2rem 1.5rem 0rem;
    min-height: 100vh;
    page-break-inside: avoid;
  }
  
  &.printable:last-child {
    page-break-after: auto;
  }
`;

const Disclaimer = styled.div`
  font-size: 0.7rem;
  color: #6c757d;
  text-align: center;
  width: 100%;
  margin-top: auto; /* Spinge il footer in fondo */
  padding: 1rem 0;
  position: relative;
  z-index: 2; /* Sopra la filigrana */
  
  &.printable {
    position: absolute;
    bottom: 1rem;
    left: 0;
    right: 0;
    font-size: 0.6rem;
    color: #333;
    margin-top: 0;
    padding: 0;
    background: rgba(255, 255, 255, 0.9); /* Sfondo semi-opaco per leggibilità */
  }
`;

const GustoHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.8rem;
`;

const GustoName = styled.h2`
  font-size: 1.4rem;
  margin: 0;
`;

const Small = styled.small`
  color: #6c757d;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  th, td { border: 1px solid #e9ecef; padding: 0.5rem; text-align: right; }
  th:first-child, td:first-child { text-align: left; }
  thead th {
    background: #f8f9fa;
    color: #2b2f33; /* colore più marcato per i titoli */
    font-weight: 700; /* font più “forte” sui titoli */
    letter-spacing: 0.02em;
  }
`;

type RicettaIngrediente = {
  ingrediente_id: number;
  nome?: string;
  ingrediente_nome?: string;
  unita?: string;
  ingrediente_unita?: string;
  quantita?: number;
  quantita_per_kg?: number;
  ordine?: number;
  is_variegatura?: boolean;
  is_aggiunta?: boolean;
};

const round = (n: number) => Math.round(n * 100) / 100;

const parseKgColumns = (value: string): number[] => {
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter(n => !isNaN(n) && n > 0);
};

const StampePage: React.FC = () => {
  const { user } = useAuth();
  const nomeAttivita = user?.tenant_name || 'Sistema Gelato';
  const [gusti, setGusti] = useState<Gusto[]>([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [kgColsInput, setKgColsInput] = useState('2,3,8');
  const kgColumns = useMemo(() => parseKgColumns(kgColsInput), [kgColsInput]);
  const [loading, setLoading] = useState(false);
  const [ricette, setRicette] = useState<Record<number, RicettaIngrediente[]>>({});
  const [soloConRicette, setSoloConRicette] = useState(false);
  const [includiDescrizione, setIncludiDescrizione] = useState(true);
  const [includiNote, setIncludiNote] = useState(true);

  // Nome del tenant per il disclaimer
  const tenantName = user?.tenant_name || user?.nome || 'Gelateria';
  const printRef = useRef<HTMLDivElement>(null);

  // Funzione per generare PDF direttamente
  const generatePDF = async () => {
    if (!printRef.current) {
      console.error('[Stampe] Elemento da stampare non trovato');
      return;
    }

    try {
      console.log('[Stampe] Iniziando generazione PDF multi-pagina...');
      
      // Trova tutte le pagine (ogni GustoPage)
      const pageElements = printRef.current.querySelectorAll('.page.printable');
      console.log(`[Stampe] Trovate ${pageElements.length} pagine da processare`);
      
      if (pageElements.length === 0) {
        console.error('[Stampe] Nessuna pagina trovata');
        return;
      }

      // Crea il PDF in orientamento orizzontale (landscape)
      const pdf = new jsPDF('l', 'mm', 'a4'); // 'l' = landscape
      const pageWidth = 297; // A4 landscape width
      const pageHeight = 210; // A4 landscape height
      
      // Processa ogni pagina separatamente
      for (let i = 0; i < pageElements.length; i++) {
        const pageElement = pageElements[i] as HTMLElement;
        console.log(`[Stampe] Processando pagina ${i + 1}/${pageElements.length}`);
        
        // Cattura la singola pagina come canvas
        const canvas = await html2canvas(pageElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: pageElement.scrollWidth,
          height: pageElement.scrollHeight
        });

        // Converti il canvas in immagine
        const imgData = canvas.toDataURL('image/png');
        
        // Calcola le dimensioni per adattare alla pagina PDF
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * pageWidth) / canvas.width;
        
        // Aggiungi nuova pagina se non è la prima
        if (i > 0) {
          pdf.addPage();
        }
        
        // Aggiungi filigrana di testo direttamente nel PDF (come backup)
        pdf.setTextColor(220, 220, 220);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        const watermarkText = `© ${new Date().getFullYear()} ${tenantName}`;
        
        // Griglia di filigrane multiple
        for (let x = 30; x < pageWidth; x += 80) {
          for (let y = 30; y < pageHeight; y += 45) {
            pdf.text(watermarkText, x, y, {
              angle: 45,
              align: 'left'
            });
          }
        }
        
        // Aggiungi l'immagine della pagina sopra la filigrana
        if (imgHeight <= pageHeight) {
          // La pagina ci sta in una pagina PDF
          pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        } else {
          // La pagina è troppo alta, adatta mantenendo proporzioni
          const scaledHeight = pageHeight;
          const scaledWidth = (canvas.width * pageHeight) / canvas.height;
          pdf.addImage(imgData, 'PNG', (pageWidth - scaledWidth) / 2, 0, scaledWidth, scaledHeight);
        }
        
        console.log(`[Stampe] Pagina ${i + 1} aggiunta al PDF`);
      }

      // Salva il PDF
      const fileName = `Ricette_${tenantName}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      console.log('[Stampe] PDF multi-pagina generato con successo:', fileName);
    } catch (error) {
      console.error('[Stampe] Errore nella generazione PDF:', error);
    }
  };

  useEffect(() => {
    (async () => {
      const data = await ApiService.getGusti(true);
      setGusti(data.filter(g => g.attivo));
    })();
  }, []);

  // Carica automaticamente le ricette all'ingresso della pagina
  useEffect(() => {
    if (gusti.length > 0) {
      console.log('[Stampe] Auto-caricamento ricette per', gusti.length, 'gusti');
      generaAnteprima(gusti.map(g => g.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-caricamento solo quando arrivano i gusti; generaAnteprima è ricreata ad ogni render
  }, [gusti]);

  // Rigenera automaticamente le ricette quando cambiano i gusti selezionati
  // DISABILITATO: Causa problemi di sparizione degli ingredienti
  // useEffect(() => {
  //   if (selected.length > 0 && kgColumns.length > 0) {
  //     console.log('[Stampe] Auto-aggiornamento ricette per gusti selezionati:', selected.length);
  //     generaAnteprima(selected);
  //   }
  // }, [selected, kgColumns]);

  const filteredGusti = useMemo(() => {
    const q = query.toLowerCase();
    let filtered = gusti
      .filter(g => g.nome.toLowerCase().includes(q) || g.categoria?.toLowerCase().includes(q))
      .sort((a,b) => a.nome.localeCompare(b.nome));
    
    // Se il filtro "solo con ricette" è attivo, controlla se il gusto ha ricette
    if (soloConRicette) {
      filtered = filtered.filter(g => {
        const ricettaGusto = ricette[g.id];
        return ricettaGusto && ricettaGusto.length > 0;
      });
    }
    
    return filtered;
  }, [gusti, query, soloConRicette, ricette]);

  const toggleSelezione = (id: number) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const generaAnteprima = async (gustiIds?: number[]) => {
    setLoading(true);
    const targetGusti = gustiIds || selected;
    console.log('[Stampe] Inizio generazione anteprima per gusti:', targetGusti);
    try {
      // Usa l'endpoint consolidato di calcolo ricette impostando 1 kg per ogni gusto selezionato
      const payload = targetGusti.map((gusto_id) => ({ gusto_id, quantita_kg: 1 }));
      console.log('[Stampe] Payload per calcola-ricette:', payload);
      let res: any;
      try {
        console.log('[Stampe] Tentativo API calcola-ricette...');
        res = await ApiService.calcolaRicette(payload);
        console.log('[Stampe] Risposta API calcola-ricette:', res);
      } catch (err) {
        console.error('[Stampe] API calcola-ricette fallita:', err);
        // Fallback: tenta il calcolo offline come fa la pagina Produzione
        try {
          console.log('[Stampe] Tentativo calcolo offline...');
          res = await ricetteOfflineService.calcolaRicetteOffline(payload);
          console.log('[Stampe] Risposta offline:', res);
        } catch (offlineErr) {
          console.error('[Stampe] Calcolo ricette fallito (API e offline):', offlineErr);
          res = null;
        }
      }
      const map: Record<number, RicettaIngrediente[]> = {};
      if (!res) {
        console.log('[Stampe] Nessuna risposta da API e offline, uso fallback per-gusto per tutti');
        // Nessuna risposta dall'API né offline: prova direttamente i fallback per-gusto
        const results = await Promise.all(
          targetGusti.map(async (id) => {
            console.log(`[Stampe] Fallback per-gusto per ID ${id}...`);
            try {
              const r = await ApiService.getRicettaGusto(id);
              console.log(`[Stampe] Risposta per-gusto ${id}:`, r);
              let list: RicettaIngrediente[] = (r.ingredienti || []).map((ing: any) => ({
                ingrediente_id: ing.ingrediente_id,
                nome: ing.nome || ing.ingrediente_nome,
                ingrediente_nome: ing.nome || ing.ingrediente_nome,
                unita: ing.unita || ing.unita_ricetta,
                ingrediente_unita: ing.unita_misura || ing.ingrediente_unita,
                quantita_per_kg: Number(ing.quantita || ing.quantita_per_kg || 0),
                ordine: ing.ordine || 0,
                is_variegatura: ing.is_variegatura || false,
                is_aggiunta: ing.is_aggiunta || false
              }));
              // fallback offline per singolo gusto
              if (!list || list.length === 0) {
                console.log(`[Stampe] Lista vuota per ${id}, provo offline singolo...`);
                try {
                  const off = await ricetteOfflineService.calcolaRicetteOffline([{ gusto_id: id, quantita_kg: 1 }]);
                  console.log(`[Stampe] Offline singolo ${id}:`, off);
                  const det = (off?.dettagli_per_gusto || []).find((d: any) => d.gusto_id === id);
                  if (det && det.ingredienti?.length > 0) {
                    list = det.ingredienti.map((ing: any) => ({
                      ingrediente_id: ing.ingrediente_id,
                      nome: ing.ingrediente_nome || ing.nome,
                      ingrediente_nome: ing.ingrediente_nome || ing.nome,
                      unita: ing.unita_ricetta || ing.unita,
                      ingrediente_unita: ing.ingrediente_unita || ing.unita_misura,
                      quantita_per_kg: Number(ing.quantita_per_kg || ing.quantita || 0),
                      ordine: ing.ordine || 0,
                      is_variegatura: ing.is_variegatura || false,
                      is_aggiunta: ing.is_aggiunta || false
                    }));
                    console.log(`[Stampe] Offline singolo ${id} ha restituito ${list.length} ingredienti`);
                  }
                } catch (e) {
                  console.error(`[Stampe] Offline singolo ${id} fallito:`, e);
                }
              }
              console.log(`[Stampe] Lista finale per ${id}: ${list.length} ingredienti`);
              return { id, list };
            } catch (err) {
              console.error(`[Stampe] Fallback per-gusto ${id} fallito:`, err);
              return { id, list: [] as RicettaIngrediente[] };
            }
          })
        );
        for (const { id, list } of results) {
          if (list.length > 0) map[id] = list;
        }
        console.log('[Stampe] Mappa finale da fallback per-gusto:', map);
      } else {
        console.log('[Stampe] Ho risposta da API/offline, costruisco mappa...');
        for (const det of res.dettagli_per_gusto || []) {
          const list: RicettaIngrediente[] = (det.ingredienti || []).map((ing: any) => ({
            ingrediente_id: ing.ingrediente_id,
            nome: ing.ingrediente_nome,
            ingrediente_nome: ing.ingrediente_nome,
            unita: ing.unita_ricetta,
            ingrediente_unita: ing.ingrediente_unita,
            quantita_per_kg: Number(ing.quantita_per_kg || 0),
            ordine: ing.ordine || 0,
            is_variegatura: ing.is_variegatura || false,
            is_aggiunta: ing.is_aggiunta || false
          }));
          map[det.gusto_id] = list;
          console.log(`[Stampe] Aggiunto alla mappa gusto ${det.gusto_id}: ${list.length} ingredienti`);
        }
      }

      // Fallback mirato: se qualche gusto risulta senza ingredienti, prova API dedicata per quel gusto
      const mancanti = selected.filter(id => !map[id] || map[id].length === 0);
      if (mancanti.length > 0) {
        try {
          const results = await Promise.all(
            mancanti.map(async (id) => {
              try {
                // 1) Tenta API per-gusto
                const r = await ApiService.getRicettaGusto(id);
                let list: RicettaIngrediente[] = (r.ingredienti || []).map((ing: any) => ({
                  ingrediente_id: ing.ingrediente_id,
                  nome: ing.nome || ing.ingrediente_nome,
                  ingrediente_nome: ing.nome || ing.ingrediente_nome,
                  // Nell'endpoint /ricette/gusto i campi sono quantita e unita
                  unita: ing.unita || ing.unita_ricetta,
                  ingrediente_unita: ing.unita_misura || ing.ingrediente_unita,
                  quantita_per_kg: Number(ing.quantita || ing.quantita_per_kg || 0),
                  ordine: ing.ordine || 0,
                  is_variegatura: ing.is_variegatura || false,
                  is_aggiunta: ing.is_aggiunta || false
                }));

                // 2) Se ancora vuoto, tenta calcolo offline per singolo gusto (se cache disponibile)
                if (!list || list.length === 0) {
                  try {
                    const offlineRes = await ricetteOfflineService.calcolaRicetteOffline([{ gusto_id: id, quantita_kg: 1 }]);
                    const det = (offlineRes?.dettagli_per_gusto || []).find((d: any) => d.gusto_id === id);
                    if (det && det.ingredienti && det.ingredienti.length > 0) {
                      list = det.ingredienti.map((ing: any) => ({
                        ingrediente_id: ing.ingrediente_id,
                        nome: ing.ingrediente_nome || ing.nome,
                        ingrediente_nome: ing.ingrediente_nome || ing.nome,
                        unita: ing.unita_ricetta || ing.unita,
                        ingrediente_unita: ing.ingrediente_unita || ing.unita_misura,
                        quantita_per_kg: Number(ing.quantita_per_kg || ing.quantita || 0),
                        ordine: ing.ordine || 0,
                        is_variegatura: ing.is_variegatura || false,
                        is_aggiunta: ing.is_aggiunta || false
                      }));
                    }
                  } catch (e) {
                    // Ignora errori offline; continueremo senza fallback
                    console.debug('[Stampe] Offline per singolo gusto non disponibile:', e);
                  }
                }

                return { id, list };
              } catch (err) {
                return { id, list: [] as RicettaIngrediente[] };
              }
            })
          );
          for (const { id, list } of results) {
            if (list.length > 0) map[id] = list;
          }
        } catch (e) {
          console.warn('[Stampe] Fallback per ricetta singola non riuscito:', e);
        }
      }
      console.log('[Stampe] Mappa ricette finale:', map);
      // Aggiorna le ricette in modo additivo invece di sovrascrivere tutto
      setRicette(prev => ({ ...prev, ...map }));
    } finally {
      setLoading(false);
    }
  };

  const onPrint = () => {
    generatePDF();
  };

  if (user?.ruolo !== 'admin') {
    return (
      <PageContainer>
        <Card>
          <CardBody>
            <PageTitle as="h1">Stampe</PageTitle>
            <p>Accesso riservato agli amministratori.</p>
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PrintContainer>
      <PageContainer>
        <Card className="no-print" style={{ marginBottom: 'var(--space-lg)' }}>
          <CardBody>
          <PageTitle as="h1" style={{ marginBottom: 'var(--space-md)' }}>Stampe</PageTitle>
          <p>Ricettario: seleziona i gusti e le colonne in kg, poi genera l'anteprima e stampa.</p>
          <Filters>
            <Field>
              <Label>Filtra gusti</Label>
              <Input placeholder="Cerca per nome o categoria" value={query} onChange={e => setQuery(e.target.value)} />
              <Small>Selezionati: {selected.length}</Small>
            </Field>
            <Field>
              <Label>Colonne in kg (es. 2,3,8)</Label>
              <Input value={kgColsInput} onChange={e => setKgColsInput(e.target.value)} />
              <Small>Usa numeri separati da virgola. Verranno create colonne per ciascun valore.</Small>
            </Field>
          </Filters>
          <Field style={{marginBottom: '1rem'}}>
            <Row style={{gap: '0.8rem'}}>
              <input 
                type="checkbox" 
                id="soloConRicette"
                checked={soloConRicette} 
                onChange={e => setSoloConRicette(e.target.checked)} 
              />
              <Label htmlFor="soloConRicette" style={{margin: 0, cursor: 'pointer'}}>
                Mostra solo gusti con ricette definite
              </Label>
            </Row>
            <Small>Filtra la lista per vedere solo i gusti che hanno ingredienti nella ricetta</Small>
          </Field>
          
          <Field style={{marginBottom: '1rem'}}>
            <Label style={{marginBottom: '0.5rem', display: 'block'}}>Includi nel PDF:</Label>
            <Row style={{gap: '1.5rem'}}>
              <Row style={{gap: '0.5rem'}}>
                <input 
                  type="checkbox" 
                  id="includiDescrizione"
                  checked={includiDescrizione} 
                  onChange={e => setIncludiDescrizione(e.target.checked)} 
                />
                <Label htmlFor="includiDescrizione" style={{margin: 0, cursor: 'pointer'}}>
                  Descrizione gusto
                </Label>
              </Row>
              <Row style={{gap: '0.5rem'}}>
                <input 
                  type="checkbox" 
                  id="includiNote"
                  checked={includiNote} 
                  onChange={e => setIncludiNote(e.target.checked)} 
                />
                <Label htmlFor="includiNote" style={{margin: 0, cursor: 'pointer'}}>
                  Note di produzione
                </Label>
              </Row>
            </Row>
            <Small>Seleziona quali informazioni aggiuntive includere nella stampa delle ricette</Small>
          </Field>
          <SelectList>
            {filteredGusti.map(g => (
              <Row key={g.id}>
                <input type="checkbox" checked={selected.includes(g.id)} onChange={() => toggleSelezione(g.id)} />
                <span>{g.nome}</span>
                <Small style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  {g.categoria}
                  {ricette[g.id] && ricette[g.id].length > 0 && (
                    <span style={{color: 'var(--success)', fontSize: '0.8rem'}}>✓ {ricette[g.id].length} ing.</span>
                  )}
                </Small>
              </Row>
            ))}
          </SelectList>
          <Actions>
            <Button $variant="secondary" onClick={() => setSelected([])}>Deseleziona tutto</Button>
            <Button $variant="secondary" onClick={() => generaAnteprima(gusti.map(g => g.id))} disabled={loading}>
              {loading ? 'Caricamento...' : 'Ricarica Ricette'}
            </Button>
            <Button onClick={onPrint} disabled={Object.keys(ricette).length === 0 || selected.length === 0}>Genera PDF</Button>
          </Actions>
          </CardBody>
        </Card>

        {/* Anteprima Ricettario */}
        <PrintableContent ref={printRef}>
        {selected
          .map(id => gusti.find(g => g.id === id))
          .filter(Boolean)
          .map((gusto) => {
            const list = ricette[gusto!.id] || [];
            return (
              <GustoPage className="page printable" key={gusto!.id}
                $nomeAttivita={nomeAttivita} data-attivita={nomeAttivita}>
                <div style={{ flex: '1 0 auto' }}>
                  <GustoHeader>
                    <GustoName>{gusto!.nome}</GustoName>
                    <Small>{gusto!.categoria}</Small>
                  </GustoHeader>
                  
                  {/* Descrizione del gusto se selezionata */}
                  {includiDescrizione && gusto!.descrizione && (
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', borderLeft: '4px solid #007bff' }}>
                      <strong>Descrizione:</strong>
                      <div style={{ marginTop: '0.5rem', lineHeight: '1.4' }}>{gusto!.descrizione}</div>
                    </div>
                  )}
                  
                  <Table>
                    <thead>
                      <tr>
                        <th>Ingrediente</th>
                        <th>Unità</th>
                        {kgColumns.map(k => (
                          <th key={k}>{k} kg</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {list.length === 0 && (
                        <tr>
                          <td colSpan={2 + kgColumns.length} style={{textAlign:'center', color:'#6c757d'}}>Nessuna ricetta definita</td>
                        </tr>
                      )}
                      {(() => {
                        const sorted = [...list].sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
                        const miscela = sorted.filter(ing => !ing.is_variegatura && !ing.is_aggiunta);
                        const variegatura = sorted.filter(ing => ing.is_variegatura);
                        const aggiunta = sorted.filter(ing => ing.is_aggiunta);
                        const haSezioni = variegatura.length > 0 || aggiunta.length > 0;

                        const renderRow = (ing: RicettaIngrediente) => {
                          const unit = ing.unita || ing.ingrediente_unita || 'g';
                          const perKg = typeof ing.quantita_per_kg === 'number' ? ing.quantita_per_kg : (ing.quantita || 0);
                          return (
                            <tr key={ing.ingrediente_id}>
                              <td>{ing.nome || ing.ingrediente_nome || ing.ingrediente_id}</td>
                              <td>{unit}</td>
                              {kgColumns.map(k => (
                                <td key={k+''}>
                                  {round(perKg * k)}
                                </td>
                              ))}
                            </tr>
                          );
                        };

                        return (
                          <>
                            {haSezioni && miscela.length > 0 && (
                              <tr>
                                <td colSpan={2 + kgColumns.length} style={{ fontWeight: 800, fontSize: '0.9rem', background: '#f8f9fa', padding: '6px 8px' }}>
                                  🍦 Miscela / Gelato
                                </td>
                              </tr>
                            )}
                            {miscela.map(renderRow)}
                            {variegatura.length > 0 && (
                              <tr>
                                <td colSpan={2 + kgColumns.length} style={{ fontWeight: 800, fontSize: '0.9rem', background: '#fff8f0', color: '#e67e22', padding: '6px 8px', borderTop: '2px dashed #f0c27a' }}>
                                  🌀 Variegatura
                                </td>
                              </tr>
                            )}
                            {variegatura.map(renderRow)}
                            {aggiunta.length > 0 && (
                              <tr>
                                <td colSpan={2 + kgColumns.length} style={{ fontWeight: 800, fontSize: '0.9rem', background: '#e1f5fe', color: '#01579b', padding: '6px 8px', borderTop: '2px dashed #03a9f4' }}>
                                  🍫 Aggiunta
                                </td>
                              </tr>
                            )}
                            {aggiunta.map(renderRow)}
                          </>
                        );
                      })()}
                    </tbody>
                  </Table>
                  
                  {/* Note di produzione se selezionate */}
                  {includiNote && gusto!.note_produzione && (
                    <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', borderLeft: '4px solid #ffc107' }}>
                      <strong>Note di produzione:</strong>
                      <div style={{ marginTop: '0.5rem', lineHeight: '1.4' }}>{gusto!.note_produzione}</div>
                    </div>
                  )}
                </div>
                
                <Disclaimer className="printable">
                  © {new Date().getFullYear()} {tenantName} - Ricetta di proprietà esclusiva. 
                  Ogni riproduzione non autorizzata è vietata dalla legge.
                </Disclaimer>
              </GustoPage>
            );
          })}
        </PrintableContent>
      </PageContainer>
    </PrintContainer>
  );
};

export default StampePage;
